import { REMOVE_ITEM_MUTATION } from '@/components/MiniCart/miniCart.gql';
import { TENTH_ANNIVERSARY_KEY_CHAINS_SKU } from '@/constants/10th-anniversary-key-chains-sku';
import { useCartContext } from '@/context/cart';
import { GET_KEY_CHAINS } from '@/typed-graphql/query';
import { generateAddToCartImage } from '@/util/generate-add-to-cart-image';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { ADD_SIMPLE_MUTATION } from './ProductFullDetail/productFullDetail.gql';

const isEndOfEvent = dayjs().isAfter(dayjs('2025-07-25 00:00:00'), 'second');

export const useResolveCartKeyChains = ({ removeCartItemMutation = REMOVE_ITEM_MUTATION } = {}) => {
  const [{ cartId }] = useCartContext();
  const [reqGetProductPageSimpleProductDetail] = useLazyQuery(gql(GET_KEY_CHAINS), { fetchPolicy: 'no-cache' });
  const [reqAddSimpleProductToCart] = useMutation(ADD_SIMPLE_MUTATION);
  const [reqRemoveItem] = useMutation(removeCartItemMutation);

  const resolveCartKeyChains = ({ cartItems, removeKeyChainsRefetchQueries, afterRemoveKeyChainsFn } = {}) => {
    if (!Array.isArray(cartItems) || cartItems.length === 0) return;

    const hasDress = cartItems.some(
      x =>
        (x?.product?.shipping_type === 'Dresses' || x?.product?.shipping_type === 'Wedding Dresses') &&
        x?.product?.is_try_on !== 1,
    );
    const cartKeyChainsUid = cartItems.find(x => x?.product?.sku === TENTH_ANNIVERSARY_KEY_CHAINS_SKU)?.uid;
    const hasKeyChains = !!cartKeyChainsUid;

    if (hasDress && !hasKeyChains && !isEndOfEvent) {
      reqGetProductPageSimpleProductDetail({
        variables: {
          sku: TENTH_ANNIVERSARY_KEY_CHAINS_SKU,
        },
      }).then(res => {
        const product = res.data?.NotVisibleProducts?.[0];

        if (!product || product.stock_status === 'OUT_OF_STOCK') return;

        reqAddSimpleProductToCart({
          variables: {
            cartId,
            sku: product.sku,
            quantity: 1,
            customizable_options: [],
            bust: '',
            category_id: 0,
            category_name: product?.categories[product?.categories.length - 1]?.name,
            customize_price: 0,
            customsize: '',
            data_sku: '',
            dresseshemline: product?.show_attribute.find(item => item.label === 'Hemline')?.value,
            extralength: '',
            height: '',
            hip: '',
            hollowtoFloor: '',
            length: '',
            product_color: '',
            product_image: generateAddToCartImage({
              mediaGalleryEntries: product.media_gallery?.map(x => ({ ...x, file: x.image })) || [],
            }),
            color_price: null,
            shipingtypes: product?.show_attribute.find(item => item.label === 'Shipping Type')?.id,
            agreement: '',
            unit: 'inch',
            watermark: 1,
          },
        });
      });

      return;
    }

    if (!hasDress && hasKeyChains) {
      reqRemoveItem({
        variables: {
          cartId,
          itemId: cartKeyChainsUid,
        },
        refetchQueries: removeKeyChainsRefetchQueries,
        awaitRefetchQueries: true,
      }).then(() => {
        afterRemoveKeyChainsFn?.();
      });
    }
  };

  return { resolveCartKeyChains };
};
